import { MapPin, Scale, Award, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const ProblemSolutionSection = () => {
  const { t } = useTranslation('home');

  // Altum's 4 Pillars
  const pillars = [
    {
      icon: MapPin,
      title: t('whyChoose.pillar1.title'),
      description: t('whyChoose.pillar1.description'),
    },
    {
      icon: Scale,
      title: t('whyChoose.pillar2.title'),
      description: t('whyChoose.pillar2.description'),
    },
    {
      icon: Award,
      title: t('whyChoose.pillar3.title'),
      description: t('whyChoose.pillar3.description'),
    },
    {
      icon: MessageSquare,
      title: t('whyChoose.pillar4.title'),
      description: t('whyChoose.pillar4.description'),
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-slate-50 dark:bg-slate-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="mb-16 text-center max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <span className="text-primary font-extrabold tracking-[0.2em] uppercase text-xs mb-4 block">
            {t('whyChoose.eyebrow')}
          </span>
          <h2 className="text-secondary dark:text-white text-4xl md:text-5xl lg:text-6xl font-heading font-black uppercase tracking-tight mb-6">
            {t('whyChoose.headline')}
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 font-medium">
            {t('whyChoose.subheadline')}
          </p>
        </motion.div>

        {/* 4-Card Grid - wider cards with less gap */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              className="group relative bg-white dark:bg-slate-800 p-8 rounded-2xl border border-slate-200 dark:border-slate-700 hover:border-primary/30 dark:hover:border-red-900/30 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 card-top-border"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            >
              {/* Icon Container */}
              <div className="mb-8 inline-flex rounded-2xl bg-slate-50 dark:bg-slate-700/50 p-5 text-primary group-hover:bg-primary group-hover:text-white transition-all duration-500 group-hover:rotate-6 shadow-sm">
                <pillar.icon className="w-8 h-8" strokeWidth={1.5} />
              </div>

              {/* Title */}
              <h3 className="text-2xl font-heading font-bold text-secondary dark:text-white mb-4">
                {pillar.title}
              </h3>

              {/* Description */}
              <p className="text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                {pillar.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSolutionSection;
